import { Badge, Box, Button, Callout, Flex, Grid, Heading, Select, Separator, Switch, Text, TextArea, TextField } from '@radix-ui/themes'
import { AlertCircle, CalendarDays, Hash, Image, Info, MapPin, Trash2, Trophy } from 'lucide-react'
import { SubmitEvent, useState } from 'react'
import { EventFormData, ManagedSeason, ManagedTrack } from '@/types/shared'
import TrixField from '@/shared/primitives/TrixField'

// An event field that overrides a venue default (e.g. parking, pit access).
// When the venue supplies the info and the event hasn't overridden it, we show
// the inherited content read-only with an "Override" button, rather than an
// empty editor — so managers see exactly what will appear on the event page.
function OverrideField({
  label,
  inheritedHtml,
  value,
  onChange,
  editorPlaceholder,
}: {
  label: string
  inheritedHtml?: string | null
  value: string
  onChange: (html: string) => void
  editorPlaceholder: string
}) {
  const hasInherited = !!inheritedHtml
  // Open the editor straight away if there's already an override, or if the
  // venue has nothing to inherit (nothing to show but the editor).
  const [overriding, setOverriding] = useState(!!value || !hasInherited)

  return (
    <Box>
      <Flex gap="2" direction="column">
        <Text size="3" weight="medium">{label}</Text>

        {overriding ? (
          <>
            {hasInherited && (
              <Flex align="center" justify="between" gap="2">
                <Text size="2" color="gray">Overriding the venue default for this event.</Text>
                <Button
                  type="button"
                  variant="ghost"
                  size="1"
                  color="gray"
                  onClick={() => { onChange(''); setOverriding(false) }}
                >
                  Use venue default
                </Button>
              </Flex>
            )}
            <TrixField value={value} onChange={onChange} placeholder={editorPlaceholder} />
          </>
        ) : (
          <>
            <Callout.Root color="gray" variant="surface">
              <Callout.Icon><Info size={16} /></Callout.Icon>
              <Callout.Text>
                Imported from the venue — this is what visitors will see. Click <strong>Override</strong> if it's different for this event.
              </Callout.Text>
            </Callout.Root>
            <Box
              className="trix-content"
              p="3"
              style={{ border: '1px solid var(--gray-5)', borderRadius: 'var(--radius-3)', background: 'var(--gray-2)' }}
              dangerouslySetInnerHTML={{ __html: inheritedHtml! }}
            />
            <Box>
              <Button type="button" variant="soft" size="2" onClick={() => setOverriding(true)}>
                Override for this event
              </Button>
            </Box>
          </>
        )}
      </Flex>
    </Box>
  )
}

// The scalar counterpart to OverrideField. Same two states — inheriting the
// venue's answer, or overriding it for this event — with two differences that
// matter: it clears to `null` rather than '' (the override columns read null as
// "inherit", and '' or false would be a decision), and whether it is overriding
// is read straight off the data instead of being kept in local state. That is
// what lets it re-read the venue default when the manager switches tracks —
// there is no stale local flag to reset.
function ScalarOverrideField({
  label,
  inheritedSummary,
  overridden,
  onOverride,
  onClear,
  children,
  overridingNote = 'Overriding the venue default for this event.',
  clearLabel = 'Use venue default',
}: {
  label: string
  /** What the venue's answer resolves to, shown while inheriting. */
  inheritedSummary: string
  overridden: boolean
  /** Seeds the override from the venue's answer, so overriding starts from what
   *  was already in force rather than from an empty field. */
  onOverride: () => void
  onClear: () => void
  children: React.ReactNode
  /** What the inherited answer comes from. Defaults to the venue; self reports
   *  inherit from this event's own application switches instead. */
  overridingNote?: string
  clearLabel?: string
}) {
  return (
    <Box>
      <Flex gap="2" direction="column">
        <Text size="3" weight="medium">{label}</Text>

        {overridden ? (
          <>
            <Flex align="center" justify="between" gap="2">
              <Text size="2" color="gray">{overridingNote}</Text>
              <Button type="button" variant="ghost" size="1" color="gray" onClick={onClear}>
                {clearLabel}
              </Button>
            </Flex>
            {children}
          </>
        ) : (
          <>
            <Callout.Root color="gray" variant="surface">
              <Callout.Icon><Info size={16} /></Callout.Icon>
              <Callout.Text>{inheritedSummary}</Callout.Text>
            </Callout.Root>
            <Box>
              <Button type="button" variant="soft" size="2" onClick={onOverride}>
                Override for this event
              </Button>
            </Box>
          </>
        )}
      </Flex>
    </Box>
  )
}

interface EventFormProps {
  data: EventFormData
  setData: <K extends keyof EventFormData>(key: K, value: EventFormData[K]) => void
  onSubmit: (e: SubmitEvent) => void
  processing: boolean
  errors?: string[]
  tracks: ManagedTrack[]
  seasons?: ManagedSeason[]
  currentSeasonIds?: number[]
  title: string
  submitLabel: string
  hasPoster?: boolean
  /** The attached poster, shown as a thumbnail. Absent on the new-event form. */
  posterUrl?: string | null
}

export default function EventForm({ data, setData, onSubmit, processing, errors, tracks, seasons = [], currentSeasonIds = [], title, submitLabel, hasPoster, posterUrl }: EventFormProps) {
  const currentSeasons = seasons.filter((s) => currentSeasonIds.includes(s.id))
  const addableSeasons = seasons.filter((s) => !currentSeasonIds.includes(s.id))
  const selectedTrack = tracks.find((t) => t.id.toString() === data.event.track_id?.toString())
  const setEvent = (fields: Partial<EventFormData['event']>) => {
    setData('event', { ...data.event, ...fields })
  }

  // What this event falls back to with no override of its own. Mirrors
  // Event#inherited_media_auto_approval? on the server.
  const inheritedAutoApproval = selectedTrack?.media_auto_approval ?? false
  const inheritedAutoApprovalLimit = selectedTrack?.media_auto_approval_limit ?? 0
  const inheritedAutoApprovalSummary = inheritedAutoApproval
    ? `Using the venue default — up to ${inheritedAutoApprovalLimit} credentialed media are approved automatically.`
    : 'Using the venue default — every media application is reviewed by hand.'

  // Mirrors Event#inherited_self_reports_allowed?: a type may self-report unless
  // this event makes it apply. Read live off the switches above, so flipping
  // "Who Can Apply" updates the summary straight away.
  const selfReportTypes = [
    { key: 'driver_self_reports_override', label: 'Drivers', plural: 'drivers', applies: data.event.accepts_drivers },
    { key: 'media_self_reports_override', label: 'Media', plural: 'media', applies: data.event.accepts_media },
  ] as const

  return (
    <Box className="border-2 py-8 px-10">
      <Heading size="7">{title}</Heading>
      <Text size="4" as="p" color="gray">
        Fill in the details below to create a new event.
      </Text>
      <Separator my="3" size="4" />

      {errors && errors.length > 0 && (
        <Callout.Root color="red" role="alert" mb="4">
          {errors.map((error, index) => (
            <div key={index}>
              <Callout.Icon>
                <AlertCircle />
              </Callout.Icon>
              <Callout.Text>{error}</Callout.Text>
            </div>
          ))}
        </Callout.Root>
      )}

      <form onSubmit={onSubmit}>
        {/* Event Details */}
        <Grid columns="1" gap="5" mb="6">
          <Box>
            <Flex gap="2" direction="column">
              <Text size="3" weight="medium">Event Name *</Text>
              <TextField.Root
                size="3"
                value={data.event.name}
                onChange={(e) => setEvent({ name: e.target.value })}
                required
                placeholder="e.g. Round 3 – Summer Showdown"
              />
            </Flex>
          </Box>

          <Box>
            <Flex gap="2" direction="column">
              <Text size="3" weight="medium">Slug</Text>
              <TextField.Root
                size="3"
                value={data.event.slug}
                onChange={(e) => setEvent({ slug: e.target.value })}
                placeholder="e.g. round-3-summer-showdown"
              />
              {/* Not derived from the name on purpose — it has to match the slug
                  the event already carries elsewhere, so a generated one would
                  look right and match nothing. */}
              <Text size="1" color="gray">
                Optional. Lowercase letters, numbers and hyphens. Leave blank unless
                this event needs to line up with an external system.
              </Text>
            </Flex>
          </Box>

          <Grid columns="2" gap="5">
            <Box>
              <Flex gap="2" direction="column">
                <Text size="3" weight="medium">Event Date *</Text>
                <TextField.Root
                  type="date"
                  size="3"
                  value={data.event.date}
                  onChange={(e) => setEvent({ date: e.target.value })}
                  required
                >
                  <TextField.Slot>
                    <CalendarDays height="16" width="16" />
                  </TextField.Slot>
                </TextField.Root>
              </Flex>
            </Box>

            <Box>
              <Flex gap="2" direction="column">
                <Text size="3" weight="medium">Track *</Text>
                <Select.Root
                  size="3"
                  value={data.event.track_id.toString()}
                  defaultValue={data.event.track_id.toString()}
                  onValueChange={(v) => {console.log(v); setEvent({ track_id: v })}}
                  required
                >
                  <Select.Trigger id="track" placeholder="Select a track…" />
                  <Select.Content>
                    {tracks.map((track) => (
                      <Select.Item key={track.id} value={track.id.toString()}>
                        {track.name}
                      </Select.Item>
                    ))}
                  </Select.Content>
                </Select.Root>
              </Flex>
            </Box>
          </Grid>
        </Grid>

        {/* Applications */}
        <Heading size="5" weight="bold">Applications</Heading>
        <Separator my="3" size="4" />
        <Grid columns="1" gap="5" mb="6">
          <Grid columns="2" gap="5">
            <Box>
              <Flex gap="2" direction="column">
                <Text size="3" weight="medium">Applications Open Date</Text>
                <Text size="2" color="gray">The date applicants can start submitting.</Text>
                <TextField.Root
                  type="date"
                  size="3"
                  value={data.event.applications_open_at}
                  onChange={(e) => setEvent({ applications_open_at: e.target.value })}
                >
                  <TextField.Slot>
                    <CalendarDays height="16" width="16" />
                  </TextField.Slot>
                </TextField.Root>
              </Flex>
            </Box>

            <Box>
              <Flex gap="2" direction="column">
                <Text size="3" weight="medium">Applications Close Date</Text>
                <Text size="2" color="gray">The last day applicants can submit. Defaults to the event date.</Text>
                <TextField.Root
                  type="date"
                  size="3"
                  value={data.event.applications_close_at}
                  onChange={(e) => setEvent({ applications_close_at: e.target.value })}
                >
                  <TextField.Slot>
                    <CalendarDays height="16" width="16" />
                  </TextField.Slot>
                </TextField.Root>
              </Flex>
            </Box>
          </Grid>

          <Box>
            <Text size="3" weight="medium" as="p" mb="3">Who Can Apply</Text>
            <Flex direction="row" justify="between">
              <Flex align="center" gap="3">
                <Switch
                  checked={data.event.accepts_drivers}
                  onCheckedChange={(v) => setEvent({ accepts_drivers: v })}
                />
                <Text size="3">Drivers</Text>
              </Flex>
              <Flex align="center" gap="3">
                <Switch
                  checked={data.event.accepts_teams}
                  onCheckedChange={(v) => setEvent({ accepts_teams: v })}
                />
                <Text size="3">Teams</Text>
              </Flex>
              <Flex align="center" gap="3">
                <Switch
                  checked={data.event.accepts_staff}
                  onCheckedChange={(v) => setEvent({ accepts_staff: v })}
                />
                <Text size="3">Staff</Text>
              </Flex>
              <Flex align="center" gap="3">
                <Switch
                  checked={data.event.accepts_media}
                  onCheckedChange={(v) => setEvent({ accepts_media: v })}
                />
                <Text size="3">Media</Text>
              </Flex>
            </Flex>
          </Box>

          <ScalarOverrideField
            label="Media Auto-Approval"
            inheritedSummary={inheritedAutoApprovalSummary}
            overridden={data.event.media_auto_approval_override !== null}
            onOverride={() =>
              setEvent({
                media_auto_approval_override: inheritedAutoApproval,
                media_auto_approval_limit_override: null,
              })
            }
            onClear={() =>
              setEvent({
                media_auto_approval_override: null,
                media_auto_approval_limit_override: null,
              })
            }
          >
            <Flex direction="column" gap="3">
              <Flex align="center" gap="3">
                <Switch
                  checked={!!data.event.media_auto_approval_override}
                  onCheckedChange={(v) => setEvent({ media_auto_approval_override: v })}
                />
                <Text size="3">Auto-approve credentialed media</Text>
              </Flex>

              {data.event.media_auto_approval_override && (
                <Flex gap="2" direction="column">
                  <Text size="2" weight="medium">Spots for this event</Text>
                  <TextField.Root
                    size="3"
                    type="number"
                    min="1"
                    style={{ maxWidth: 160 }}
                    placeholder={inheritedAutoApprovalLimit.toString()}
                    value={data.event.media_auto_approval_limit_override ?? ''}
                    onChange={(e) =>
                      setEvent({
                        media_auto_approval_limit_override:
                          e.target.value === '' ? null : Math.max(0, Number(e.target.value)),
                      })
                    }
                  />
                  <Text size="2" color="gray">
                    Leave blank to keep the venue's number ({inheritedAutoApprovalLimit}). Counts
                    auto-approvals only — you can still approve more by hand, and media past the
                    number are waitlisted and approved automatically if a spot frees up.
                  </Text>
                </Flex>
              )}
            </Flex>
          </ScalarOverrideField>
        </Grid>

        {/* Self reports */}
        <Heading size="5" weight="bold">Self reports</Heading>
        <Separator my="3" size="4" />
        <Text size="2" color="gray" as="p" mb="4">
          Lets signed-in drivers and media say "I'm going" before the event and "I was there" on the
          day. Nobody approves these; the event page says they're self-reported.
        </Text>
        <Grid columns="1" gap="5" mb="6">
          {selfReportTypes.map(({ key, label, plural, applies }) => {
            const inherited = !applies
            return (
              <ScalarOverrideField
                key={key}
                label={label}
                inheritedSummary={
                  inherited
                    ? `On — ${plural} don't apply to this event, so they can say they're going or were there.`
                    : `Off — ${plural} apply to this event, so only approved applicants are listed.`
                }
                overridden={data.event[key] !== null}
                onOverride={() => setEvent({ [key]: inherited })}
                onClear={() => setEvent({ [key]: null })}
                overridingNote={`Overriding the default for ${plural} at this event.`}
                clearLabel="Use the default"
              >
                <Flex align="center" gap="3">
                  <Switch
                    checked={!!data.event[key]}
                    onCheckedChange={(v) => setEvent({ [key]: v })}
                  />
                  <Text size="3">Let {plural} self-report</Text>
                </Flex>
              </ScalarOverrideField>
            )
          })}
        </Grid>

        {/* Publishing */}
        <Text size="5" weight="bold">Publishing</Text>
        <Separator my="3" size="4" />
        <Grid columns="1" gap="5" mb="6">
          <Box>
            <Flex gap="2" direction="column">
              <Text size="3" weight="medium">Publish At</Text>
              <Text size="2" color="gray">
                Leave blank to save as a draft. Set a future date/time to schedule publishing.
              </Text>
              <TextField.Root
                type="datetime-local"
                size="3"
                value={data.event.publish_at}
                onChange={(e) => setEvent({ publish_at: e.target.value })}
              >
                <TextField.Slot>
                  <CalendarDays height="16" width="16" />
                </TextField.Slot>
              </TextField.Root>
            </Flex>
          </Box>
        </Grid>

        {/* Content */}
        <Text size="5" weight="bold">Content</Text>
        <Separator my="3" size="4" />
        <Grid columns="1" gap="5" mb="6">
          <Box>
            <Flex gap="2" direction="column">
              <Text size="3" weight="medium">Description</Text>
              <TrixField
                value={data.event.description}
                onChange={(html) => setEvent({ description: html })}
                placeholder="Tell people what this event is about…"
              />
            </Flex>
          </Box>

          <OverrideField
            key={`price-${selectedTrack?.id ?? 'none'}`}
            label="Price"
            inheritedHtml={selectedTrack?.prices_html}
            value={data.event.price}
            onChange={(html) => setEvent({ price: html })}
            editorPlaceholder="e.g. $150 drivers / $50 spectators"
          />

          <Box>
            <Flex gap="2" direction="column">
              <Text size="3" weight="medium">Schedule</Text>
              <TrixField
                value={data.event.schedule}
                onChange={(html) => setEvent({ schedule: html })}
                placeholder="e.g. 7am Gates open / 8am Driver's meeting / 9am First runs…"
              />
            </Flex>
          </Box>

          <Box>
            <Flex gap="2" direction="column">
              <Text size="3" weight="medium">Instagram Hashtag</Text>
              <TextField.Root
                size="3"
                value={data.event.instagram_hashtag}
                onChange={(e) => setEvent({ instagram_hashtag: e.target.value })}
                placeholder="e.g. SummerShowdown2026"
              >
                <TextField.Slot>
                  <Hash height="16" width="16" />
                </TextField.Slot>
              </TextField.Root>
            </Flex>
          </Box>

          <Box>
            <Flex gap="2" direction="column">
              <Text size="3" weight="medium">Social Links</Text>
              <Text size="2" color="gray">Comma-separated URLs (Instagram, YouTube, Facebook, TikTok, Threads, X, Flickr, or any website). We'll pick the right icon for each.</Text>
              <TextField.Root
                size="3"
                value={data.event.social_urls}
                onChange={(e) => setEvent({ social_urls: e.target.value })}
                placeholder="https://instagram.com/p/…, https://youtube.com/watch?v=…"
              />
            </Flex>
          </Box>
        </Grid>

        {/* Event-specific notes + venue overrides */}
        <Heading size="5" weight="bold">Event-Specific Notes</Heading>
        <Separator my="3" size="4" />
        <Grid columns="1" gap="5" mb="6">
          <Box>
            <Flex gap="2" direction="column">
              <Text size="3" weight="medium">Notes</Text>
              <Text size="2" color="gray">Pinned at the top of the event page as a callout. Use this for one-off info like "spectators use the back gate this weekend".</Text>
              <TrixField
                value={data.event.notes}
                onChange={(html) => setEvent({ notes: html })}
                placeholder="One-off info for this event…"
              />
            </Flex>
          </Box>

          <OverrideField
            key={`parking-${selectedTrack?.id ?? 'none'}`}
            label="Parking"
            inheritedHtml={selectedTrack?.parking_html}
            value={data.event.parking_override}
            onChange={(html) => setEvent({ parking_override: html })}
            editorPlaceholder="Event-specific parking instructions…"
          />

          <OverrideField
            key={`pit-access-${selectedTrack?.id ?? 'none'}`}
            label="Pit Access"
            inheritedHtml={selectedTrack?.pit_access_html}
            value={data.event.pit_access_override}
            onChange={(html) => setEvent({ pit_access_override: html })}
            editorPlaceholder="Event-specific pit access rules…"
          />
        </Grid>

        {/* Poster */}
        <Text size="5" weight="bold">Event Poster</Text>
        <Separator my="3" size="4" />
        <Grid columns="1" gap="5" mb="6">
          <Box>
            <Flex gap="2" direction="column">
              <Text size="3" weight="medium">Poster Image</Text>
              <Text size="2" color="gray">Displayed on the event detail page. Recommended 1200×630px.</Text>
              {hasPoster && !data.event.remove_poster && (
                <Flex gap="3" align="center">
                  {posterUrl && (
                    <img
                      src={posterUrl}
                      alt="Current event poster"
                      style={{
                        display: 'block',
                        maxWidth: '8rem',
                        maxHeight: '10rem',
                        objectFit: 'contain',
                        border: '1px solid var(--gray-5)',
                        borderRadius: 'var(--radius-3)',
                      }}
                    />
                  )}
                  <Flex direction="column" gap="1" align="start">
                    <Text size="2" color="indigo">Current poster — upload below to replace it.</Text>
                    <Button
                      type="button"
                      variant="ghost"
                      size="1"
                      color="red"
                      onClick={() => setEvent({ poster: null, remove_poster: true })}
                    >
                      <Trash2 size={12} />
                      Remove poster
                    </Button>
                  </Flex>
                </Flex>
              )}

              {/* Staged, not immediate: the poster goes when the manager saves,
                  the same as every other change on this form. */}
              {hasPoster && data.event.remove_poster && (
                <Flex gap="2" align="center">
                  <Text size="2" color="red">Poster will be removed when you save.</Text>
                  <Button
                    type="button"
                    variant="ghost"
                    size="1"
                    color="gray"
                    onClick={() => setEvent({ remove_poster: false })}
                  >
                    Undo
                  </Button>
                </Flex>
              )}
              <Flex align="center" gap="2">
                <Image height="16" width="16" />
                <input
                  type="file"
                  accept="image/*"
                  // Choosing a replacement cancels a staged removal — uploading
                  // one and deleting the old one are the same intent.
                  onChange={(e) => setEvent({ poster: e.target.files?.[0] ?? null, remove_poster: false })}
                  style={{ fontSize: 'var(--font-size-2)' }}
                />
              </Flex>
            </Flex>
          </Box>
        </Grid>

        {/* Season */}
        <Heading size="5" weight="bold">Season</Heading>
        <Separator my="3" size="4" />
        <Grid columns="1" gap="5" mb="6">
          <Box>
            <Flex gap="2" direction="column">
              <Text size="3" weight="medium">Add to Season</Text>
              <Text size="2" color="gray">
                Optionally include this event in a championship or series. To remove or manage memberships, use the season's Manage Events page.
              </Text>
              {currentSeasons.length > 0 && (
                <Flex gap="2" wrap="wrap" align="center">
                  <Text size="2" color="gray">Currently in:</Text>
                  {currentSeasons.map((s) => (
                    <Badge key={s.id} variant="soft" size="2">
                      <Trophy size={12} />
                      {s.name}
                    </Badge>
                  ))}
                </Flex>
              )}
              <Select.Root
                size="3"
                value={data.event.season_id || "none"}
                onValueChange={(v) => setEvent({ season_id: v === "none" ? "" : v })}
              >
                <Select.Trigger placeholder="None" />
                <Select.Content>
                  <Select.Item value="none">None</Select.Item>
                  {addableSeasons.map((season) => (
                    <Select.Item key={season.id} value={season.id.toString()}>
                      {season.name}{season.track_name ? ` — ${season.track_name}` : ''}
                    </Select.Item>
                  ))}
                </Select.Content>
              </Select.Root>
            </Flex>
          </Box>
        </Grid>

        <Box mt="6">
          <Flex gap="4" justify="end">
            <Button type="button" size="3" color="gray" variant="soft" onClick={() => history.back()}>
              Cancel
            </Button>
            <Button size="3" type="submit" disabled={processing}>
              {submitLabel}
            </Button>
          </Flex>
        </Box>
      </form>
    </Box>
  )
}
